import React from 'react';
import { Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Animation keyframes
const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-20px) rotate(5deg);
  }
  66% {
    transform: translateY(10px) rotate(-3deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
`;

const drift = keyframes`
  0% {
    transform: translateX(-100px) translateY(0px) rotate(0deg);
  }
  100% {
    transform: translateX(100px) translateY(-20px) rotate(360deg);
  }
`;

const slideInOut = keyframes`
  0%, 100% {
    transform: translateX(-200px) rotate(45deg);
    opacity: 0;
  }
  50% {
    transform: translateX(200px) rotate(90deg);
    opacity: 0.4;
  }
`;

const morphing = keyframes`
  0%, 100% {
    border-radius: 50% 40% 60% 30%;
    transform: rotate(0deg);
  }
  25% {
    border-radius: 30% 60% 40% 70%;
    transform: rotate(90deg);
  }
  50% {
    border-radius: 60% 30% 70% 40%;
    transform: rotate(180deg);
  }
  75% {
    border-radius: 40% 70% 30% 60%;
    transform: rotate(270deg);
  }
`;

// Main container for animated background
const AnimatedContainer = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: 0,
}));

// Gateway Icon Component
const GatewayIcon = styled(Box)<{ size?: number; color?: string; delay?: number }>(
  ({ size = 40, color = 'rgba(255, 255, 255, 0.1)', delay = 0 }) => ({
    width: size,
    height: size,
    position: 'absolute',
    '&::before': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '100%',
      border: `2px solid ${color}`,
      borderRadius: '8px',
      animation: `${float} 6s ease-in-out infinite`,
      animationDelay: `${delay}s`,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '60%',
      height: '60%',
      transform: 'translate(-50%, -50%)',
      border: `1px solid ${color}`,
      borderRadius: '4px',
      animation: `${pulse} 4s ease-in-out infinite`,
      animationDelay: `${delay + 1}s`,
    },
  })
);

// Circuit Pattern Component
const CircuitPattern = styled(Box)<{ color?: string; delay?: number }>(
  ({ color = 'rgba(255, 255, 255, 0.08)', delay = 0 }) => ({
    position: 'absolute',
    width: '100px',
    height: '2px',
    backgroundColor: color,
    '&::before': {
      content: '""',
      position: 'absolute',
      width: '6px',
      height: '6px',
      backgroundColor: color,
      borderRadius: '50%',
      top: '-2px',
      left: '20%',
      animation: `${pulse} 3s ease-in-out infinite`,
      animationDelay: `${delay}s`,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      width: '4px',
      height: '4px',
      backgroundColor: color,
      borderRadius: '50%',
      top: '-1px',
      right: '30%',
      animation: `${pulse} 3s ease-in-out infinite`,
      animationDelay: `${delay + 0.5}s`,
    },
    animation: `${drift} 15s linear infinite`,
    animationDelay: `${delay}s`,
  })
);

// Data Flow Component
const DataFlow = styled(Box)<{ color?: string; size?: number; delay?: number }>(
  ({ color = 'rgba(46, 125, 50, 0.15)', size = 20, delay = 0 }) => ({
    position: 'absolute',
    width: size,
    height: size,
    backgroundColor: color,
    borderRadius: '50%',
    animation: `${slideInOut} 12s ease-in-out infinite`,
    animationDelay: `${delay}s`,
  })
);

// Currency Symbol Component
const CurrencySymbol = styled(Box)<{
  symbol?: '₹' | '$' | '€' | '£' | '¥';
  size?: number;
  color?: string;
  delay?: number;
}>(({ symbol = '₹', size = 24, color = 'rgba(255, 255, 255, 0.1)', delay = 0 }) => ({
  position: 'absolute',
  fontSize: size,
  fontWeight: 'bold',
  color,
  fontFamily: 'serif',
  animation: `${float} 6s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  '&::before': {
    content: `"${symbol}"`,
  },
}));

// Coin Component
const Coin = styled(Box)<{ size?: number; color?: string; delay?: number }>(
  ({ size = 30, color = 'rgba(255, 215, 0, 0.2)', delay = 0 }) => ({
    position: 'absolute',
    width: size,
    height: size,
    borderRadius: '50%',
    background: `radial-gradient(circle at 30% 30%, ${color}, rgba(255, 215, 0, 0.05))`,
    border: `2px solid ${color}`,
    '&::before': {
      content: '"₹"',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: size * 0.5,
      fontWeight: 'bold',
      color: color.replace('0.2', '0.4'),
    },
    animation: `${float} 8s ease-in-out infinite`,
    animationDelay: `${delay}s`,
  })
);

// Credit Card Component
const CreditCard = styled(Box)<{ color?: string; delay?: number }>(
  ({ color = 'rgba(255, 255, 255, 0.08)', delay = 0 }) => ({
    position: 'absolute',
    width: '40px',
    height: '25px',
    backgroundColor: color,
    borderRadius: '4px',
    border: `1px solid ${color.replace('0.08', '0.15')}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '4px',
      left: '4px',
      width: '8px',
      height: '6px',
      backgroundColor: color.replace('0.08', '0.3'),
      borderRadius: '2px',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '4px',
      left: '4px',
      right: '4px',
      height: '1px',
      backgroundColor: color.replace('0.08', '0.2'),
    },
    animation: `${drift} 12s linear infinite`,
    animationDelay: `${delay}s`,
  })
);

// Bank Icon Component
const BankIcon = styled(Box)<{ size?: number; color?: string; delay?: number }>(
  ({ size = 35, color = 'rgba(255, 255, 255, 0.1)', delay = 0 }) => ({
    position: 'absolute',
    width: size,
    height: size,
    '&::before': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '8px',
      backgroundColor: color,
      borderRadius: '0 0 4px 4px',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: `${size * 0.4}px solid transparent`,
      borderRight: `${size * 0.4}px solid transparent`,
      borderBottom: `${size * 0.3}px solid ${color}`,
    },
    // Pillars
    background: `
      linear-gradient(${color} 0%, ${color} 100%) 20% 40% / 3px 60% no-repeat,
      linear-gradient(${color} 0%, ${color} 100%) 35% 40% / 3px 60% no-repeat,
      linear-gradient(${color} 0%, ${color} 100%) 50% 40% / 3px 60% no-repeat,
      linear-gradient(${color} 0%, ${color} 100%) 65% 40% / 3px 60% no-repeat,
      linear-gradient(${color} 0%, ${color} 100%) 80% 40% / 3px 60% no-repeat
    `,
    animation: `${pulse} 7s ease-in-out infinite`,
    animationDelay: `${delay}s`,
  })
);

// Money Transfer Arrow Component
const TransferArrow = styled(Box)<{ color?: string; delay?: number }>(
  ({ color = 'rgba(46, 125, 50, 0.15)', delay = 0 }) => ({
    position: 'absolute',
    width: '50px',
    height: '3px',
    backgroundColor: color,
    borderRadius: '2px',
    '&::before': {
      content: '""',
      position: 'absolute',
      right: '-8px',
      top: '-4px',
      width: 0,
      height: 0,
      borderLeft: `12px solid ${color}`,
      borderTop: '5px solid transparent',
      borderBottom: '5px solid transparent',
    },
    '&::after': {
      content: '"₹"',
      position: 'absolute',
      left: '-15px',
      top: '-8px',
      fontSize: '12px',
      color: color.replace('0.15', '0.3'),
      fontWeight: 'bold',
    },
    animation: `${slideInOut} 10s ease-in-out infinite`,
    animationDelay: `${delay}s`,
  })
);

// Wallet Component
const Wallet = styled(Box)<{ color?: string; delay?: number }>(
  ({ color = 'rgba(255, 255, 255, 0.1)', delay = 0 }) => ({
    position: 'absolute',
    width: '32px',
    height: '24px',
    backgroundColor: color,
    borderRadius: '4px',
    border: `1px solid ${color.replace('0.1', '0.2')}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '6px',
      left: '4px',
      right: '4px',
      height: '2px',
      backgroundColor: color.replace('0.1', '0.3'),
      borderRadius: '1px',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '12px',
      left: '4px',
      right: '4px',
      height: '2px',
      backgroundColor: color.replace('0.1', '0.25'),
      borderRadius: '1px',
    },
    animation: `${morphing} 9s ease-in-out infinite`,
    animationDelay: `${delay}s`,
  })
);

// Digital Payment Wave Component
const PaymentWave = styled(Box)<{ color?: string; delay?: number }>(
  ({ color = 'rgba(46, 125, 50, 0.12)', delay = 0 }) => ({
    position: 'absolute',
    width: '60px',
    height: '20px',
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '2px',
      backgroundColor: color,
      borderRadius: '2px',
    },
    '&::before': {
      top: '6px',
      transform: 'scaleX(0.8)',
      animation: `${pulse} 3s ease-in-out infinite`,
      animationDelay: `${delay}s`,
    },
    '&::after': {
      top: '12px',
      transform: 'scaleX(0.6)',
      animation: `${pulse} 3s ease-in-out infinite`,
      animationDelay: `${delay + 0.5}s`,
    },
    animation: `${drift} 14s linear infinite`,
    animationDelay: `${delay}s`,
  })
);

// Network Node Component
const NetworkNode = styled(Box)<{ color?: string; delay?: number }>(
  ({ color = 'rgba(255, 255, 255, 0.1)', delay = 0 }) => ({
    position: 'absolute',
    width: '12px',
    height: '12px',
    backgroundColor: color,
    borderRadius: '50%',
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      width: '40px',
      height: '1px',
      backgroundColor: color,
      top: '50%',
      left: '50%',
    },
    '&::before': {
      transform: 'translate(-50%, -50%) rotate(45deg)',
    },
    '&::after': {
      transform: 'translate(-50%, -50%) rotate(-45deg)',
    },
    animation: `${pulse} 5s ease-in-out infinite`,
    animationDelay: `${delay}s`,
  })
);

export interface AnimatedBackgroundProps {
  theme?: 'light' | 'dark';
  intensity?: 'low' | 'medium' | 'high';
}

export const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({
  theme = 'dark',
  intensity = 'medium',
}) => {
  const isLight = theme === 'light';
  const baseColor = isLight ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.08)';
  const accentColor = isLight ? `rgba(46, 125, 50, 0.1)` : `rgba(46, 125, 50, 0.15)`;

  const elementCount = {
    low: {
      gateways: 3,
      circuits: 2,
      flows: 4,
      currencies: 3,
      coins: 2,
      cards: 2,
      banks: 1,
      transfers: 2,
      wallets: 2,
      waves: 3,
      nodes: 3,
    },
    medium: {
      gateways: 5,
      circuits: 4,
      flows: 6,
      currencies: 5,
      coins: 4,
      cards: 3,
      banks: 2,
      transfers: 4,
      wallets: 3,
      waves: 5,
      nodes: 5,
    },
    high: {
      gateways: 8,
      circuits: 6,
      flows: 10,
      currencies: 8,
      coins: 6,
      cards: 5,
      banks: 3,
      transfers: 6,
      wallets: 5,
      waves: 8,
      nodes: 8,
    },
  };

  const counts = elementCount[intensity];

  return (
    <AnimatedContainer>
      {/* Gateway Icons */}
      {Array.from({ length: counts.gateways }).map((_, i) => (
        <GatewayIcon
          key={`gateway-${i}`}
          size={30 + Math.random() * 20}
          color={i % 2 === 0 ? baseColor : accentColor}
          delay={i * 2}
          sx={{
            top: `${10 + Math.random() * 80}%`,
            left: `${5 + Math.random() * 90}%`,
          }}
        />
      ))}

      {/* Circuit Patterns */}
      {Array.from({ length: counts.circuits }).map((_, i) => (
        <CircuitPattern
          key={`circuit-${i}`}
          color={baseColor}
          delay={i * 3}
          sx={{
            top: `${Math.random() * 100}%`,
            left: `-100px`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}

      {/* Data Flow Elements */}
      {Array.from({ length: counts.flows }).map((_, i) => (
        <DataFlow
          key={`flow-${i}`}
          size={8 + Math.random() * 12}
          color={accentColor}
          delay={i * 1.5}
          sx={{
            top: `${Math.random() * 100}%`,
            left: `-50px`,
          }}
        />
      ))}

      {/* Currency Symbols */}
      {Array.from({ length: counts.currencies }).map((_, i) => {
        const symbols = ['₹', '$', '€', '£', '¥'] as const;
        const symbol = symbols[i % symbols.length];

        return (
          <CurrencySymbol
            key={`currency-${i}`}
            symbol={symbol}
            size={20 + Math.random() * 15}
            color={i % 2 === 0 ? baseColor : accentColor}
            delay={i * 1.8}
            sx={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
          />
        );
      })}

      {/* Coins */}
      {Array.from({ length: counts.coins }).map((_, i) => (
        <Coin
          key={`coin-${i}`}
          size={25 + Math.random() * 20}
          color="rgba(255, 215, 0, 0.15)"
          delay={i * 2.2}
          sx={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
          }}
        />
      ))}

      {/* Credit Cards */}
      {Array.from({ length: counts.cards }).map((_, i) => (
        <CreditCard
          key={`card-${i}`}
          color={baseColor}
          delay={i * 3.5}
          sx={{
            top: `${Math.random() * 100}%`,
            left: `-50px`,
            transform: `rotate(${Math.random() * 30 - 15}deg)`,
          }}
        />
      ))}

      {/* Bank Icons */}
      {Array.from({ length: counts.banks }).map((_, i) => (
        <BankIcon
          key={`bank-${i}`}
          size={30 + Math.random() * 15}
          color={accentColor}
          delay={i * 4}
          sx={{
            top: `${20 + Math.random() * 60}%`,
            left: `${Math.random() * 100}%`,
          }}
        />
      ))}

      {/* Money Transfer Arrows */}
      {Array.from({ length: counts.transfers }).map((_, i) => (
        <TransferArrow
          key={`transfer-${i}`}
          color={accentColor}
          delay={i * 2.8}
          sx={{
            top: `${Math.random() * 100}%`,
            left: `-60px`,
            transform: `rotate(${Math.random() * 60 - 30}deg)`,
          }}
        />
      ))}

      {/* Wallets */}
      {Array.from({ length: counts.wallets }).map((_, i) => (
        <Wallet
          key={`wallet-${i}`}
          color={baseColor}
          delay={i * 2.5}
          sx={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
          }}
        />
      ))}

      {/* Payment Waves */}
      {Array.from({ length: counts.waves }).map((_, i) => (
        <PaymentWave
          key={`wave-${i}`}
          color={accentColor}
          delay={i * 1.8}
          sx={{
            top: `${Math.random() * 100}%`,
            left: `-70px`,
            transform: `rotate(${Math.random() * 20 - 10}deg)`,
          }}
        />
      ))}

      {/* Network Nodes */}
      {Array.from({ length: counts.nodes }).map((_, i) => (
        <NetworkNode
          key={`node-${i}`}
          color={baseColor}
          delay={i * 2.5}
          sx={{
            top: `${20 + Math.random() * 60}%`,
            left: `${10 + Math.random() * 80}%`,
          }}
        />
      ))}
    </AnimatedContainer>
  );
};

export default AnimatedBackground;
